import React, { useEffect, useState } from 'react';

function cueLabel(label) {
  return String(label || '').replace(/_/g, ' ').trim();
}

function DurationLine({ duration }) {
  if (!duration) return null;
  return (
    <p className="text-xs text-[#9CA3AF]" data-testid="screenplay-duration">
      Estimated {duration.estimated_seconds}s · target {duration.target_seconds}s · gap {duration.gap_seconds}s
    </p>
  );
}

function DurationWarning({ duration, busy, onLengthen }) {
  const gap = Number(duration?.gap_seconds);
  if (!duration || !(gap < 0)) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-950/30 px-3 py-2" data-testid="screenplay-duration-warning">
      <p className="text-xs text-amber-100">
        About {Math.abs(gap)}s short. You can still approve this version.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => onLengthen?.()}
        className="shrink-0 px-3 py-1.5 rounded-full bg-amber-200 text-[#1C1917] text-[10px] font-bold cursor-pointer disabled:opacity-60"
        data-testid="make-it-longer"
      >
        Make it longer
      </button>
    </div>
  );
}

function CueChip({ label, busy, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);

  useEffect(() => {
    if (!editing) setDraft(label);
  }, [label, editing]);

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft.trim() !== label) onSave?.(draft.trim());
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            setDraft(label);
            setEditing(false);
          }
        }}
        className="w-36 bg-white/[0.06] border border-[#93C5FD]/40 rounded-full px-2 py-0.5 text-[10px] text-white outline-none"
      />
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#1E3A5F]/60 border border-[#93C5FD]/20 pl-2 pr-1 py-0.5" data-testid="screenplay-cue">
      <button
        type="button"
        disabled={busy}
        onClick={() => setEditing(true)}
        className="text-[10px] text-[#BFDBFE] cursor-pointer"
      >
        [SFX: {label}]
      </button>
      <button
        type="button"
        disabled={busy}
        aria-label="Remove cue"
        onClick={onRemove}
        className="w-4 h-4 rounded-full text-[#93C5FD] hover:text-white cursor-pointer text-[10px]"
      >
        ×
      </button>
    </span>
  );
}

function EditableText({
  value,
  onSave,
  busy,
  testId,
  className,
  label,
  multiline = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const close = (next) => {
    setEditing(false);
    if (String(next) !== String(value || '')) onSave?.(next);
  };

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={testId}
        disabled={busy}
        onClick={() => setEditing(true)}
        className={`block w-full text-left cursor-text disabled:cursor-default ${className || ''}`}
      >
        {label ? <span className="block text-[10px] uppercase tracking-wide text-[#6B7280] mb-0.5">{label}</span> : null}
        <span className="whitespace-pre-wrap">{value}</span>
      </button>
    );
  }

  const fieldClass = 'w-full bg-white/[0.06] border border-[#2DD4BF]/40 rounded-lg px-2 py-1.5 text-xs text-white outline-none';
  if (multiline) {
    return (
      <textarea
        autoFocus
        rows={2}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => close(draft)}
        className={fieldClass}
      />
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => close(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(value || '');
          setEditing(false);
        }
      }}
      className={fieldClass}
    />
  );
}

export function screenplayVoiceRows(plan) {
  const cast = plan?.cast || {};
  const rows = (Array.isArray(cast.characters) ? cast.characters : []).map((member) => ({
    key: member?.id || member?.name,
    name: member?.name || 'Speaker',
    voice: member?.voice || null,
  }));
  const narrator = cast.narrator || {};
  if (narrator.character_id || narrator.name) {
    rows.push({ key: 'narrator', name: narrator.name || 'Narrator', voice: narrator.voice || null });
  }
  return rows;
}

export function StructuredScreenplayReview({
  screenplay,
  staleNotice,
  reapprovalRequired,
  busy,
  onGenerate,
  onEditLine,
  onEditCue,
  onEditScene,
  onInstruction,
  onSpeaker,
  onLengthen,
  onApprove,
  speakers = [],
  onChangeVoices,
}) {
  const [instruction, setInstruction] = useState('');
  const scenes = screenplay?.scenes || [];
  const validation = screenplay?.validation || {};
  const problems = validation.problems || [];
  const proposed = screenplay?.proposed_speakers || validation.proposed_speakers || [];
  const locked = screenplay?.status === 'locked';
  const stale = screenplay?.status === 'stale' || Boolean(staleNotice);
  const showCues = Boolean(screenplay?.include_sfx);

  if (!scenes.length) {
    return (
      <div className="space-y-3" data-testid="structured-screenplay-review">
        <p className="text-sm text-white">Write the screenplay</p>
        <p className="text-xs text-[#9CA3AF]">This becomes the script for the story.</p>
        <button
          type="button"
          disabled={busy}
          onClick={onGenerate}
          className="w-full py-2.5 rounded-xl bg-[#2DD4BF] text-[#06080D] text-xs font-bold cursor-pointer disabled:opacity-60"
          data-testid="structured-screenplay-write"
        >
          {busy ? 'Writing…' : 'Write screenplay'}
        </button>
      </div>
    );
  }

  const cuesFor = (sceneId) => (screenplay.cues || []).filter((cue) => cue.scene_id === sceneId);

  return (
    <div className="space-y-4" data-testid="structured-screenplay-review">
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold text-white">Screenplay</h3>
          <p className="text-[10px] text-[#6B7280]" data-testid="screenplay-version">
            {locked ? 'Approved' : 'Draft'} · {screenplay.screenplay_id}
          </p>
        </div>
        {screenplay.synopsis ? (
          <p className="text-xs text-[#E5E7EB]" data-testid="screenplay-synopsis">{screenplay.synopsis}</p>
        ) : null}
        <DurationLine duration={validation.duration} />
        <p className="text-[10px] text-[#6B7280]">Click a line to edit it. Changes save when you leave the line.</p>
      </div>

      <DurationWarning duration={validation.duration} busy={busy} onLengthen={onLengthen} />

      {speakers.length ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="screenplay-voices">
          {speakers.map((speaker) => (
            <span
              key={speaker.key || speaker.name}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px]"
              data-testid={`screenplay-voice-${speaker.key || speaker.name}`}
            >
              <span className="text-white">{speaker.name}</span>
              <span className="text-[#9CA3AF]">{speaker.voice?.name || speaker.voice?.voice_id || 'No voice yet'}</span>
            </span>
          ))}
          {onChangeVoices ? (
            <button
              type="button"
              disabled={busy}
              onClick={onChangeVoices}
              className="px-2.5 py-1 rounded-full border border-white/15 text-[11px] text-[#E5E7EB] cursor-pointer disabled:opacity-60"
              data-testid="screenplay-change-voices"
            >
              Change voices
            </button>
          ) : null}
        </div>
      ) : null}

      {stale ? (
        <p className="rounded-xl border border-amber-400/40 bg-amber-950/40 px-3 py-2 text-xs text-amber-100" data-testid="screenplay-stale-notice">
          {staleNotice || 'The context or format changed. The screenplay needs to be written again.'}
        </p>
      ) : null}
      {reapprovalRequired ? (
        <p className="rounded-xl border border-amber-400/40 bg-amber-950/40 px-3 py-2 text-xs text-amber-100" data-testid="screenplay-reapproval-notice">
          This version changed after approval. Approve it again before production.
        </p>
      ) : null}
      {problems.length ? (
        <ul className="rounded-xl border border-amber-400/40 bg-amber-950/40 px-3 py-2 text-xs text-amber-100 space-y-1" data-testid="screenplay-problems">
          {problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      ) : null}
      {proposed.map((speaker) => {
        const name = speaker.name || speaker.display_name || 'speaker';
        return (
          <div key={name} className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-xs text-[#E5E7EB]" data-testid="screenplay-proposed-speaker">
            <span className="flex-1">{name} is proposed and cannot speak until you decide.</span>
            <button type="button" disabled={busy} onClick={() => onSpeaker?.(name, 'approve')} className="px-2.5 py-1 rounded-full bg-[#2DD4BF] text-[#06080D] text-[10px] font-bold cursor-pointer" data-testid={`approve-speaker-${name}`}>Approve</button>
            <button type="button" disabled={busy} onClick={() => onSpeaker?.(name, 'reject')} className="px-2.5 py-1 rounded-full border border-white/15 text-[10px] cursor-pointer" data-testid={`reject-speaker-${name}`}>Reject</button>
          </div>
        );
      })}

      <div className="space-y-3">
        {scenes.map((scene, index) => {
          const named = {};
          (scene.dialogue || []).forEach((line) => {
            if (line.speaker_entity_id && line.speaker_name) named[line.speaker_entity_id] = line.speaker_name;
          });
          const names = [
            ...(scene.characters || []).map((item) => item.display_name || item.name || named[item.entity_id]).filter(Boolean),
            ...(scene.dialogue || []).map((line) => line.speaker_name).filter(Boolean),
          ];
          const cast = [...new Set(names)];
          const cues = showCues ? cuesFor(scene.scene_id) : [];
          return (
            <section key={scene.scene_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="screenplay-scene">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">
                  Scene {index + 1}{scene.word_budget ? ` · ${scene.word_budget} words` : ''}
                </p>
                <EditableText
                  value={scene.scene_heading || `Scene ${index + 1}`}
                  busy={busy}
                  className="text-sm font-semibold text-white"
                  onSave={(sceneHeading) => onEditScene?.({
                    scene_id: scene.scene_id,
                    scene_heading: sceneHeading,
                    purpose: scene.purpose || '',
                  })}
                />
                {scene.purpose ? (
                  <EditableText
                    value={scene.purpose}
                    busy={busy}
                    className="text-xs text-[#9CA3AF]"
                    onSave={(purpose) => onEditScene?.({
                      scene_id: scene.scene_id,
                      scene_heading: scene.scene_heading || '',
                      purpose,
                    })}
                  />
                ) : null}
              </div>
              {cast.length ? <p className="text-[10px] text-[#6B7280]" data-testid="screenplay-cast">With {cast.join(', ')}</p> : null}
              {(scene.visual_action || []).map((action) => (
                <p key={action.action_id} className="text-xs italic text-[#9CA3AF]" data-testid="screenplay-action">
                  {action.description}{action.shot_suggestion ? ` (${action.shot_suggestion})` : ''}
                </p>
              ))}
              {(scene.dialogue || []).map((line) => (
                <EditableText
                  key={line.line_id}
                  testId="screenplay-dialogue"
                  label={line.speaker_name || 'Speaker'}
                  value={line.text}
                  busy={busy}
                  multiline
                  className="text-sm text-white"
                  onSave={(text) => onEditLine?.(line.line_id, text)}
                />
              ))}
              {(scene.narration || []).map((line) => (
                <EditableText
                  key={line.line_id}
                  testId="screenplay-narration"
                  label="Narration"
                  value={line.text}
                  busy={busy}
                  multiline
                  className="text-sm text-[#DDD6FE]"
                  onSave={(text) => onEditLine?.(line.line_id, text)}
                />
              ))}
              {cues.length ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {cues.map((cue) => (
                    <CueChip
                      key={cue.cue_id}
                      label={cueLabel(cue.label)}
                      busy={busy}
                      onSave={(label) => onEditCue?.(cue.cue_id, label)}
                      onRemove={() => onEditCue?.(cue.cue_id, '')}
                    />
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      <div className="flex gap-2 items-end">
        <label className="block flex-1 text-[10px] text-[#9CA3AF]">
          Rewrite
          <input
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            placeholder="Make the ending more hopeful"
            className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-2 py-2 text-xs text-white"
            data-testid="screenplay-instruction"
          />
        </label>
        <button
          type="button"
          disabled={busy || !instruction.trim()}
          onClick={() => onInstruction?.(instruction.trim())}
          className="px-3 py-2 rounded-lg border border-white/15 text-xs text-white cursor-pointer disabled:opacity-40"
          data-testid="screenplay-instruction-send"
        >
          Apply
        </button>
      </div>
      <button
        type="button"
        disabled={busy || stale || locked || validation.ok === false}
        onClick={() => onApprove?.(screenplay.screenplay_id)}
        className="w-full py-2.5 rounded-xl bg-[#2DD4BF] text-[#06080D] text-xs font-bold cursor-pointer disabled:opacity-60"
        data-testid="approve-screenplay"
      >
        {locked ? 'Approved' : 'Approve screenplay'}
      </button>
    </div>
  );
}
